import React from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { AlertTriangle, DollarSign, MapPin, Car } from 'lucide-react';

interface DeletePricingConfirmationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pricingDetails: string;
  pricingId: number;
  onConfirm: (pricingId: number) => void;
  isLoading?: boolean;
}

const DeletePricingConfirmation: React.FC<DeletePricingConfirmationProps> = ({
  open,
  onOpenChange,
  pricingDetails,
  pricingId,
  onConfirm,
  isLoading = false,
}) => {
  const handleConfirm = () => {
    onConfirm(pricingId);
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" />
            <AlertDialogTitle>Delete Pricing Rule</AlertDialogTitle>
          </div>
          <AlertDialogDescription className="text-left">
            Are you sure you want to delete the pricing rule for <span className="font-semibold">{pricingDetails}</span>?
            This action cannot be undone and will permanently remove:
            <ul className="mt-2 ml-4 list-disc space-y-1 text-sm">
              <li>All pricing configuration data</li>
              <li>Base fare and rate calculations</li>
              <li>Minimum fare and night charges</li>
              <li>Toll fee settings</li>
              <li>Historical pricing data and analytics</li>
            </ul>
            <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive font-medium">
                <strong>Warning:</strong> This action is permanent and cannot be reversed.
                Active rides using this pricing rule may be affected.
              </p>
            </div>
            <div className="mt-3 flex items-center gap-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
              <div className="flex items-center gap-2 text-amber-700">
                <MapPin className="w-4 h-4" />
                <Car className="w-4 h-4" />
                <DollarSign className="w-4 h-4" />
              </div>
              <p className="text-sm text-amber-800">
                <strong>Impact:</strong> Riders and drivers in this location will need fallback pricing until a new rule is created.
              </p>
            </div>
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={isLoading}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={isLoading}
            className="bg-destructive hover:bg-destructive/90"
          >
            {isLoading ? 'Deleting...' : 'Delete Pricing Rule'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

export default DeletePricingConfirmation;